import React from 'react'
import PropTypes from 'prop-types'
import clientData from '../../../utils/client-data'
import glamorous from 'glamorous'
import {
  HeadingText,
  Divider,
  ShowAltIcon,
  EditIcon,
  grey600,
  grey700,
  v2green500,
  v2grey600,
  white,
  Link,
  v2blue400,
} from '@paypalcorp/pp-react'
import { get } from 'lodash'
import getContent from 'pp-react-l10n'
import SocialShare from '../social/share'
const l10n = getContent('pages/seller/dashboard')

const Container = glamorous.div({
  margin: '-30px 0 0 0',
  '& div[role=heading]': {
    textAlign: 'center',
    position: 'absolute',
    width: '60%',
    left: '50%',
    marginLeft: '-30%',
    top: -85,
  },
})
const ContentBlock = glamorous.div({
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  padding: '0 13%',
  '& p': {
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  },
  '@media (max-width: 414px)': {
    padding: '0 3%',
  },
  '& .edit': {
    margin: '30px 0 20px 0',
  },
  '& .preview': {
    margin: '10px 0 20px 0',
  },
  '& .edit a, & .preview a': {
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
    fontSize: '1rem',
    fontWeight: 'normal',
    '& span': {
      color: grey700,
    },
  },
})

const BackgroundPhoto = glamorous.div(({ image }) => ({
  boxSizing: 'border-box',
  width: '100%',
  '& > div:first-child': {
    backgroundImage: `url(${encodeURI(image)})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: '100%',
    paddingBottom: '32%',
    maxHeight: 293,
    position: 'relative',
  },
}))

const Logo = glamorous.div(({ image }) => ({
  height: '20vw',
  width: '20vw',
  maxHeight: 124,
  maxWidth: 124,
  borderRadius: '50%',
  backgroundImage: `url(${encodeURI(image)})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'absolute',
  left: 'calc(7% + 10px)',
  bottom: 0,
  transform: 'translateY(50%)',
  border: white + ' solid 3px',
  boxSizing: 'content-box',
}))

const SellerName = glamorous.h2({
  fontSize: '1.5rem',
  fontWeight: 'normal',
  lineHeight: '2rem',
  margin: '15% 0 5px 0',
  padding: '10px 0 0 0',
})

const ShareLinkTxt = glamorous.p({
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  color: grey600,
})

const HandleLink = glamorous.a({
  color: v2blue400,
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  fontSize: '1rem',
  lineHeight: '1.5rem',
})

const ActivityWidgetWrapper = glamorous.div({
  '& #js_widgetView': {
    fontFamily:
      'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif !important',
  },
  // activity widget styles override
  '& #js_widgetView .transactionRow': {
    paddingLeft: 0,
  },
  '& #js_widgetView .newActivityModule .moduleHeader': {
    padding: 0,
  },
  '& #js_widgetView .newActivityModule .activityBubble': {
    paddingLeft: 0,
  },
  '& #js_widgetView .transactionItem .transactionAmount': {
    color: v2green500,
  },
})

const ActivityWidget = glamorous.div({
  marginTop: '1.25rem',
  '& .recent-activity-header-link': {
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: v2grey600,
    fontWeight: 'normal',
    paddingLeft: '0.5rem',
  },
  '& .recent-activity-header-link:after': {
    paddingRight: '0.5rem',
  },
})

Dashboard.defaultProps = {
  sellerName: get(
    clientData,
    'sellerProfileDetails.user_info.alternate_full_name',
  ),
  sellerHandle: get(clientData, 'sellerProfileDetails.id'),
  backgroundImg: get(
    clientData,
    'sellerProfileDetails.personalization_settings.cover_photo_url',
  ),
  logoImg: get(clientData, 'sellerProfileDetails.user_info.profile_photo_url'),
  buttonsData: {
    copyLink: `https://paypal.me/${get(
      clientData,
      'sellerProfileDetails.id',
    )}?locale.x=${get(clientData, 'locality.locale')}`,
    facebookLink: 'https://facebook.com',
    messengerLink: 'https://messenger.com',
    twitterLink: 'https://twitter.com',
  },
}

Dashboard.propTypes = {
  sellerName: PropTypes.string.isRequired,
  sellerHandle: PropTypes.string.isRequired,
  backgroundImg: PropTypes.string.isRequired,
  logoImg: PropTypes.string.isRequired,
  buttonsData: PropTypes.object.isRequired,
}

function Dashboard(props) {
  const mql = window.matchMedia('(max-width: 480px)')
  let mobileView = mql.matches

  const advanceToPreview = (e) => {
    e.preventDefault()
    props.history.push(`/seller/${props.sellerHandle}/dashboard/preview`)
  }

  const advanceToEdit = (e) => {
    e.preventDefault()
    props.history.push(`/seller/${props.sellerHandle}/settings`)
  }

  return (
    <Container data-test-id="seller-dashboard-screen">
      <HeadingText
        size={mobileView ? 'sm' : 'md'}
        role="heading"
        aria-level="1"
      >
        {l10n('title')}
      </HeadingText>
      <BackgroundPhoto image={props.backgroundImg}>
        {/* inner div here for maintaining the aspect ratio*/}
        <div role="banner">
          <Logo image={props.logoImg} />
        </div>
      </BackgroundPhoto>
      <ContentBlock>
        <SellerName role="heading" aria-level="2">
          {props.sellerName}
        </SellerName>
        <HandleLink href="preview" onClick={advanceToPreview}>
          {props.sellerHandle}
        </HandleLink>
        <div className="edit">
          <Link
            className="edit"
            href={window.location.href.replace('/dashboard', '/settings')}
            onClick={advanceToEdit}
            leftIcon={<EditIcon size="sm" />}
            theme="v2"
          >
            {l10n('edit')}
          </Link>
        </div>
        <div className="preview">
          <Link
            href="preview"
            onClick={advanceToPreview}
            leftIcon={<ShowAltIcon size="sm" />}
            theme="v2"
          >
            {l10n('preview')}
          </Link>
        </div>

        <Divider secondary />
        <ShareLinkTxt id="shareLinkTitle">{l10n('shareLink')}</ShareLinkTxt>
        <SocialShare
          copyLink={props.buttonsData.copyLink}
          sellerName={props.sellerName}
        />
        <Divider secondary />
        <ActivityWidgetWrapper>
          <ActivityWidget
            data-test-id="activityWidget"
            style={{ scrollBehavior: 'smooth', overflow: 'scroll' }}
            dangerouslySetInnerHTML={{ __html: clientData.activityWidgetHtml }}
          />
        </ActivityWidgetWrapper>
      </ContentBlock>
    </Container>
  )
}
export default Dashboard
